/*
 * Copyright (c) 2025 SARL SIGMA POLYNESIA
 */

import { Menu, PopoverProps } from '@mantine/core'
import RequirePerm from '../../auth/components/RequirePerm'
import CreateMenuItem from './CreateMenuItem'
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser'
import { getCustomerCreateLink } from '../lib/links/customerLinks'
import { faFileContract } from '@fortawesome/free-solid-svg-icons/faFileContract'
import { getMandateCreateLink } from '../lib/links/mandateLinks'
import { faHome } from '@fortawesome/free-solid-svg-icons/faHome'
import { getEstateCreateLink } from '../lib/links/estateLinks'
import { faBriefcase } from '@fortawesome/free-solid-svg-icons/faBriefcase'
import { getProspectingCreateLink } from '../lib/links/prospectingLinks'
import { faHouseCircleCheck } from '@fortawesome/free-solid-svg-icons/faHouseCircleCheck'
import { getInvoiceCreateLink } from '../lib/links/invoiceLinks'
import { INVOICE_SALE } from '../enums/InvoiceTypes.mjs'
import { faFolder } from '@fortawesome/free-solid-svg-icons/faFolder'
import { getLeaseCreateLink } from '../lib/links/leaseLinks'
import { faFileInvoice } from '@fortawesome/free-solid-svg-icons/faFileInvoice'
import { faCircleUp } from '@fortawesome/free-solid-svg-icons/faCircleUp'
import { getTransactionCreateLink } from '../lib/links/transactionLinks'
import { faCircleDown } from '@fortawesome/free-solid-svg-icons/faCircleDown'
import { faTools } from '@fortawesome/free-solid-svg-icons/faTools'
import { getInterventionCreateLink } from '../lib/links/interventionLinks'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import { getManagementReportGenerateLink } from '../lib/links/managementReportLinks'
import { faFileLines } from '@fortawesome/free-solid-svg-icons/faFileLines'

export type CreateMenuProps = PopoverProps

function CreateMenu (props: CreateMenuProps): ReactElement {
  const { children, ...others } = props
  const intl = useIntl()
  const [opened, setOpened] = useState(false)
  const location = useLocation()
  const ref = useRef(false)

  useEffect(() => {
    if (location.pathname && ref.current) {
      setOpened(false)
    }
  }, [location.pathname, opened])

  useEffect(() => {
    ref.current = opened && !ref.current
  }, [opened])

  return (
    <Menu
      shadow="xl"
      trapFocus
      {...others}
      onChange={setOpened}
      opened={opened}
      withArrow
    >
      <Menu.Target>
        {children}
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>
          {intl.formatMessage({ id: 'commercial' })}
        </Menu.Label>
        <RequirePerm
          permissions={['read:customer', 'write:customer']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'contact' })}
              icon={faUser}
              to={getCustomerCreateLink()}
            />
          )}
        />
        <RequirePerm
          permissions={['read:mandate', 'write:mandate']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'mandate' })}
              icon={faFileContract}
              to={getMandateCreateLink()}
            />
          )}
        />
        <RequirePerm
          permissions={['read:estate', 'write:estate']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'estate' })}
              icon={faHome}
              to={getEstateCreateLink()}
            />
          )}
        />
        <RequirePerm
          permissions={['read:prospecting', 'write:prospecting']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'prospecting' })}
              icon={faBriefcase}
              to={getProspectingCreateLink()}
            />
          )}
        />
        <RequirePerm
          permissions={['read:invoice', 'write:invoice']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'sale' })}
              icon={faHouseCircleCheck}
              to={getInvoiceCreateLink({ type: INVOICE_SALE })}
            />
          )}
        />

        <Menu.Label>
          {intl.formatMessage({ id: 'management' })}
        </Menu.Label>
        <RequirePerm
          permissions={['read:lease', 'write:lease']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'lease' })}
              icon={faFolder}
              to={getLeaseCreateLink()}
            />
          )}
        />
        <RequirePerm
          permissions={['read:invoice', 'write:invoice']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'invoice' })}
              icon={faFileInvoice}
              to={getInvoiceCreateLink()}
            />
          )}
        />
        <RequirePerm
          permissions={['read:transaction', 'write:transaction']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'expense' })}
              icon={faCircleUp}
              to={getTransactionCreateLink(true)}
            />
          )}
        />
        <RequirePerm
          permissions={['read:transaction', 'write:transaction']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'revenue' })}
              icon={faCircleDown}
              to={getTransactionCreateLink(false)}
            />
          )}
        />
        <RequirePerm
          permissions={['read:management-report', 'write:management-report']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'management_report' })}
              icon={faFileLines}
              to={getManagementReportGenerateLink()}
            />
          )}
        />
        <RequirePerm
          permissions={['read:intervention', 'write:intervention']}
          render={() => (
            <CreateMenuItem
              label={intl.formatMessage({ id: 'intervention' })}
              icon={faTools}
              to={getInterventionCreateLink()}
            />
          )}
        />
      </Menu.Dropdown>
    </Menu>
  )
}

export default CreateMenu
